import { useEffect, useMemo, useState, type ChangeEvent, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AppLayout } from "../components/AppLayout.tsx";
import { Button } from "../components/ui/Button.tsx";
import { useAuth } from "../lib/auth.tsx";
import { apiRequest, ApiError } from "../lib/api.ts";
import { BulkConfigDialog } from "./devices/BulkConfigDialog.tsx";
import { BulkOtaDialog } from "./devices/BulkOtaDialog.tsx";

// Wire-format device row — dates arrive as ISO strings, not Date objects.
interface DeviceListEntry {
  mac: string;
  product_type: string;
  variant: string | null;
  firmware_version: string | null;
  status: "online" | "offline" | "never_seen";
  last_seen: string | null;
  pending_sync: boolean;
}

interface DeviceListResponse {
  rows: DeviceListEntry[];
  total: number;
  limit: number;
  offset: number;
}

const PAGE_SIZE = 50;

const STATUS_LABEL: Record<DeviceListEntry["status"], string> = {
  online: "Online",
  offline: "Offline",
  never_seen: "Never seen",
};

const STATUS_DOT: Record<DeviceListEntry["status"], string> = {
  online: "bg-g0",
  offline: "bg-text3",
  never_seen: "bg-g1",
};

function formatRelative(iso: string | null): string {
  if (!iso) return "—";
  const ts = new Date(iso).getTime();
  if (Number.isNaN(ts)) return "—";
  // Clamped: last_seen is the device's own reading timestamp, which the server
  // accepts up to 15 minutes ahead — unclamped that renders "-26s ago".
  const s = Math.max(0, Math.floor((Date.now() - ts) / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

export default function DevicesPage() {
  const { token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const product = searchParams.get("product") ?? "";
  const status = searchParams.get("status") ?? "";
  const firmware = searchParams.get("firmware") ?? "";
  const search = searchParams.get("search") ?? "";
  const offset = Number(searchParams.get("offset") ?? "0");

  const [data, setData] = useState<DeviceListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Selection persists across pages until the admin clears it explicitly. We
  // track the product_type alongside each MAC so the bulk-OTA dialog can show
  // the per-product breakdown without re-fetching the rows.
  const [selected, setSelected] = useState<Map<string, string>>(new Map());
  const [bulkConfigOpen, setBulkConfigOpen] = useState(false);
  const [bulkOtaOpen, setBulkOtaOpen] = useState(false);

  // Local form mirror so typing into the search box doesn't refire the query
  // on every keystroke — applied on submit.
  const [searchInput, setSearchInput] = useState(search);
  useEffect(() => setSearchInput(search), [search]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const params = new URLSearchParams();
    if (product) params.set("product", product);
    if (status) params.set("status", status);
    if (firmware) params.set("firmware", firmware);
    if (search) params.set("search", search);
    params.set("limit", String(PAGE_SIZE));
    params.set("offset", String(offset));

    apiRequest<DeviceListResponse>(`/admin/devices?${params.toString()}`, { token })
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const msg =
          err instanceof ApiError ? err.message : "Failed to load devices.";
        setError(msg);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [token, product, status, firmware, search, offset]);

  function setFilter(key: string, value: string) {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    next.delete("offset"); // reset to first page on any filter change
    setSearchParams(next, { replace: true });
  }

  function onSearchSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setFilter("search", searchInput.trim());
  }

  function gotoPage(nextOffset: number) {
    const next = new URLSearchParams(searchParams);
    if (nextOffset === 0) next.delete("offset");
    else next.set("offset", String(nextOffset));
    setSearchParams(next, { replace: true });
  }

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  const pageRows = data?.rows ?? [];
  const pageSelectedCount = pageRows.filter((d) => selected.has(d.mac)).length;
  const pageAllSelected = pageRows.length > 0 && pageSelectedCount === pageRows.length;
  const pageSomeSelected = pageSelectedCount > 0 && !pageAllSelected;

  function toggleRow(d: DeviceListEntry) {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(d.mac)) next.delete(d.mac);
      else next.set(d.mac, d.product_type);
      return next;
    });
  }

  function togglePage() {
    setSelected((prev) => {
      const next = new Map(prev);
      if (pageAllSelected) {
        for (const d of pageRows) next.delete(d.mac);
      } else {
        for (const d of pageRows) next.set(d.mac, d.product_type);
      }
      return next;
    });
  }

  function clearSelection() {
    setSelected(new Map());
  }

  // Per-product breakdown of the current selection — feeds the bulk OTA
  // dialog so admins can see how many of each product they picked.
  const productBreakdown = useMemo(() => {
    const acc: Record<string, number> = {};
    for (const product of selected.values()) {
      acc[product] = (acc[product] ?? 0) + 1;
    }
    return acc;
  }, [selected]);

  const selectedMacs = useMemo(() => [...selected.keys()], [selected]);

  return (
    <AppLayout>
      <div className="px-6 py-6">
        <div className="mb-[18px] flex items-end justify-between gap-4">
          <div>
            <h1 className="text-[21px] font-bold tracking-tight text-text">Devices</h1>
            <p className="mt-0.5 text-[13px] text-text2">
              {loading
                ? "Loading…"
                : data
                  ? `${data.total} device${data.total === 1 ? "" : "s"} registered`
                  : ""}
            </p>
          </div>
        </div>

        <Filters
          product={product}
          status={status}
          firmware={firmware}
          searchInput={searchInput}
          onSearchInputChange={setSearchInput}
          onSearchSubmit={onSearchSubmit}
          onFilter={setFilter}
        />

        {error ? (
          <div className="mt-4 rounded-[9px] border border-g3/40 bg-g3/10 px-4 py-3 text-sm text-g3">
            {error}
          </div>
        ) : null}

        {selected.size > 0 ? (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-[11px] border border-brand1/40 bg-brand1/[0.08] px-[14px] py-2.5 text-sm">
            <div className="text-text">
              <span className="font-bold">{selected.size}</span> selected
              <span className="ml-3 text-xs text-text2">
                {Object.entries(productBreakdown)
                  .map(([p, n]) => `${p}: ${n}`)
                  .join(" · ")}
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => setBulkConfigOpen(true)}>
                Push config…
              </Button>
              <Button onClick={() => setBulkOtaOpen(true)}>
                Push OTA…
              </Button>
              <Button onClick={clearSelection}>Clear</Button>
            </div>
          </div>
        ) : null}

        <div className="mt-4 overflow-x-auto rounded-card border border-line bg-surface shadow-[var(--shadow-sm)]">
          <table className="w-full border-collapse text-left text-[13px]">
            <thead>
              <tr>
                <th className="w-[34px] border-b border-line bg-bg px-[14px] py-[11px]">
                  <input
                    type="checkbox"
                    aria-label="Select all on this page"
                    checked={pageAllSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = pageSomeSelected;
                    }}
                    onChange={togglePage}
                    className="h-[15px] w-[15px] cursor-pointer accent-brand1"
                  />
                </th>
                <Th>MAC</Th>
                <Th>Product</Th>
                <Th>Variant</Th>
                <Th>Firmware</Th>
                <Th>Status</Th>
                <Th>Last seen</Th>
              </tr>
            </thead>
            <tbody>
              {!loading && data && data.rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-text3">
                    {product || status || firmware || search
                      ? "No devices match these filters."
                      : "No devices registered yet."}
                  </td>
                </tr>
              ) : null}
              {data?.rows.map((d) => {
                const checked = selected.has(d.mac);
                return (
                  <tr
                    key={d.mac}
                    className={
                      "border-b border-line transition last:border-0 hover:bg-soft " +
                      (checked ? "bg-brand1/[0.08]" : "")
                    }
                  >
                    <td className="px-[14px] py-[11px]">
                      <input
                        type="checkbox"
                        aria-label={`Select ${d.mac}`}
                        checked={checked}
                        onChange={() => toggleRow(d)}
                        className="h-[15px] w-[15px] cursor-pointer accent-brand1"
                      />
                    </td>
                    <Td>
                      <Link
                        to={`/devices/${d.mac}`}
                        className="font-mono text-[13px] font-semibold text-accent hover:underline"
                      >
                        {d.mac}
                      </Link>
                    </Td>
                    <Td>{d.product_type}</Td>
                    <Td className="text-text2">{d.variant ?? "—"}</Td>
                    <Td className="font-mono text-text2">{d.firmware_version ?? "—"}</Td>
                    <Td>
                      <span className="inline-flex items-center gap-2">
                        <span className={`h-2 w-2 rounded-full ${STATUS_DOT[d.status]}`} />
                        {STATUS_LABEL[d.status]}
                      </span>
                    </Td>
                    <Td className="font-mono text-text2">{formatRelative(d.last_seen)}</Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {bulkConfigOpen ? (
          <BulkConfigDialog
            macs={selectedMacs}
            onClose={() => setBulkConfigOpen(false)}
            onPushed={clearSelection}
          />
        ) : null}
        {bulkOtaOpen ? (
          <BulkOtaDialog
            macs={selectedMacs}
            productBreakdown={productBreakdown}
            onClose={() => setBulkOtaOpen(false)}
            onPushed={clearSelection}
          />
        ) : null}

        {data && data.total > PAGE_SIZE ? (
          <div className="mt-4 flex items-center justify-between text-[13px] text-text2">
            <div>
              Page {currentPage} of {totalPages}
            </div>
            <div className="flex gap-2">
              <Button onClick={() => gotoPage(Math.max(0, offset - PAGE_SIZE))} disabled={offset === 0}>
                Previous
              </Button>
              <Button onClick={() => gotoPage(offset + PAGE_SIZE)} disabled={offset + PAGE_SIZE >= data.total}>
                Next
              </Button>
            </div>
          </div>
        ) : null}
      </div>
    </AppLayout>
  );
}

interface FiltersProps {
  product: string;
  status: string;
  firmware: string;
  searchInput: string;
  onSearchInputChange: (v: string) => void;
  onSearchSubmit: (e: FormEvent<HTMLFormElement>) => void;
  onFilter: (key: string, value: string) => void;
}

function Filters(props: FiltersProps) {
  const onSelect =
    (key: string) =>
    (e: ChangeEvent<HTMLSelectElement | HTMLInputElement>) =>
      props.onFilter(key, e.target.value);

  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
      <form onSubmit={props.onSearchSubmit}>
        <FieldLabel>Search MAC</FieldLabel>
        <input
          value={props.searchInput}
          onChange={(e) => props.onSearchInputChange(e.target.value)}
          placeholder="HAZOSENSE_…"
          className={INPUT_CLASS}
        />
      </form>
      <div>
        <FieldLabel>Product</FieldLabel>
        <input
          value={props.product}
          onChange={onSelect("product")}
          placeholder="hazosense"
          className={INPUT_CLASS}
        />
      </div>
      <div>
        <FieldLabel>Status</FieldLabel>
        <select value={props.status} onChange={onSelect("status")} className={INPUT_CLASS}>
          <option value="">All</option>
          <option value="online">Online</option>
          <option value="offline">Offline</option>
          <option value="never_seen">Never seen</option>
        </select>
      </div>
      <div>
        <FieldLabel>Firmware</FieldLabel>
        <input
          value={props.firmware}
          onChange={onSelect("firmware")}
          placeholder="1.0.0"
          className={INPUT_CLASS}
        />
      </div>
    </div>
  );
}

const INPUT_CLASS =
  "mt-1.5 w-full rounded-[9px] border border-line bg-bg px-[11px] py-2 text-[13px] text-text outline-none transition focus:border-brand1 focus:ring-2 focus:ring-brand1/25";

function FieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <label className="block text-[10.5px] font-semibold uppercase tracking-[0.06em] text-text3">
      {children}
    </label>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="whitespace-nowrap border-b border-line bg-bg px-[14px] py-[11px] text-left text-[10px] font-semibold uppercase tracking-[0.07em] text-text3">
      {children}
    </th>
  );
}

function Td({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  return <td className={`px-[14px] py-[11px] ${className}`}>{children}</td>;
}
